import { parsePhoneNumberFromString, PhoneNumber } from "libphonenumber-js";

// Shared phone number helpers. Phone data in the platform is unvalidated free text
// coming back from the APIs, so everything here has to survive null, empty string,
// the "-" placeholder some grids pass in, and numbers that simply don't parse.

const DEFAULT_COUNTRY = "US";

/** Placeholder values callers use to mean "no number". */
const EMPTY_VALUES = ["", "-", "--", "n/a", "none"];

/** Normalizes caller input to a trimmed string, or "" when there is no number. */
const toRawValue = (phone?: string | number | null): string => {
  if (phone === null || phone === undefined) {
    return "";
  }
  const raw = String(phone).trim();
  return EMPTY_VALUES.includes(raw.toLowerCase()) ? "" : raw;
};

/**
 * Parses to a PhoneNumber only when the result is actually dialable. libphonenumber
 * happily parses "123" into +1123, which would otherwise become a dead tel: link,
 * so anything that fails isValid() is treated as unparsed and passed through as-is.
 */
const parseValid = (raw: string): PhoneNumber | undefined => {
  if (!raw) {
    return undefined;
  }
  const parsed = parsePhoneNumberFromString(raw, DEFAULT_COUNTRY);
  return parsed?.isValid() ? parsed : undefined;
};

/** True when the value holds something we can render as a callable number. */
export const hasPhoneNumber = (phone?: string | number | null): boolean =>
  toRawValue(phone) !== "";

/**
 * Display format for a phone number: "(406) 555-0120" for US numbers,
 * "+44 20 7183 8750" for international ones so the country code isn't lost.
 * Returns the raw input unchanged when it can't be parsed, and "" when there is no number.
 */
export const formatPhoneNumber = (phone?: string | number | null): string => {
  const raw = toRawValue(phone);
  if (!raw) {
    return "";
  }

  const parsed = parseValid(raw);
  if (!parsed) {
    return raw;
  }

  // Both formats already append the extension when the source included one.
  return parsed.country === DEFAULT_COUNTRY
    ? parsed.formatNational()
    : parsed.formatInternational();
};

/**
 * The value for a `tel:` href — E.164 ("+14065550120") when the number is valid,
 * with ";ext=" appended for extensions. Falls back to digits-only for numbers that
 * don't parse, and "" when there is nothing callable.
 */
export const toTelHref = (phone?: string | number | null): string => {
  const raw = toRawValue(phone);
  if (!raw) {
    return "";
  }

  const parsed = parseValid(raw);
  if (parsed) {
    return parsed.ext ? `${parsed.number};ext=${parsed.ext}` : parsed.number;
  }

  return raw.replace(/\D/g, "");
};

/** Digits in a US national number — the length every phone form validates against. */
export const PHONE_DIGIT_LENGTH = 10;

/** Characters a fully masked number occupies: "(406) 555-0120". */
export const PHONE_MASK_LENGTH = 14;

/**
 * Reduces anything a user can type or paste to the bare digits we store and send:
 * "(406) 555-0120", "+1 406 555 0120" and "406.555.0120" all collapse to "4065550120".
 *
 * A leading "1" is only a country code when it makes the value 11 digits long — dropping it
 * unconditionally would eat the first digit of a number that legitimately starts with one.
 * Anything past the 10th digit is discarded so the result always satisfies the
 * `length === PHONE_DIGIT_LENGTH` checks the forms already run.
 */
export const toPhoneDigits = (value?: string | number | null): string => {
  const digits = toRawValue(value).replace(/\D/g, "");
  const national =
    digits.length > PHONE_DIGIT_LENGTH && digits.startsWith("1")
      ? digits.slice(1)
      : digits;
  return national.slice(0, PHONE_DIGIT_LENGTH);
};

/**
 * The input mask, applied progressively as the user types: "" → "(4" → "(406) 555" →
 * "(406) 555-0120". Built by hand rather than with libphonenumber's AsYouType because
 * AsYouType formats partial input differently, which would let the mask drift away from
 * formatPhoneNumber. At 10 digits this returns exactly what formatPhoneNumber returns.
 */
export const maskPhoneInput = (value?: string | number | null): string => {
  const digits = toPhoneDigits(value);
  if (digits.length <= 3) {
    // No closing paren until the area code is complete, otherwise backspacing fights the mask.
    return digits.length === 0 ? "" : `(${digits}`;
  }
  if (digits.length <= 6) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  }
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};
